// The submissions list body — toolbar, table, footer — lifted out of
// SubmissionsPage so the Inspections → Submissions tab renders the SAME list
// instead of a second copy that could drift. Everything that differs between the
// two surfaces is a prop; search, sort, selection and paging live here.
//
// The endpoint is generic over the parent record: `type` names the KIND of
// record `recordIds` point at, so /submissions asks for one form's submissions
// while the inspection tab asks for every inspection's.
//
// Deliberate gaps, all pending backend contracts:
// - The Inspections / Tasks pills carry no counts: the response returns a single
//   totalCount for the active filter, no per-source totals.
// - The selection bar's More menu is fully live — PDF report (pdf render of
//   the single selected row), PDF versions (that row's version history modal),
//   Export CSV (POST export-manager/export-submissions, a fire-and-forget
//   job), Archive/Restore and Delete (POST submissions/bulk-action).
// - COMMENTS / LOGS row icons are coming-soon on /submissions; on a surface
//   that hides them via `hiddenColumns` they don't render at all.

import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import {
  Button,
  Card,
  ConfirmModal,
  DataTable,
  FilterDropdown,
  Icon,
  IconButton,
  Input,
  TableFooter,
  type FilterDropdownCategory,
  type FilterDropdownDateRange,
} from '@/components/common';
import useDebouncedValue from '@/hooks/useDebouncedValue';
import useModuleAccess, { useCanExportReports } from '@/hooks/useModuleAccess';
import useStickyTotal from '@/hooks/useStickyTotal';
import {
  SubmissionBulkAction,
  SubmissionFlowState,
  SubmissionListStatus,
  SubmissionReportType,
  SubmissionSortKey,
  SubmissionSourceFilter,
  useBulkSubmissionActionMutation,
  useExportSubmissionAttachmentsMutation,
  useExportSubmissionsCsvMutation,
  useGetAccessedSubmissionsQuery,
  useGetSubmissionReportQuery,
  useLazyGetSubmissionPdfUrlQuery,
  type Submission,
  type SubmissionSourceType,
} from '@/api/endpoints/submissionsApi';
import type { ApiErrorShape } from '@/api/types';
import { downloadFileUrl } from '@/utils/downloadUrl';
import { FormDateFilter } from '@/api/endpoints/formsApi';
import { useGetStructureSelectionQuery } from '@/api/endpoints/structureApi';
import { useGetUsersSelectionQuery } from '@/api/endpoints/usersApi';
import {
  DATE_ANY,
  buildDateFilterOptions,
  dateFilterOf,
  dateRangeOf,
} from '@/utils/dateFilterPresets';
import type { OrgUnit } from '@/mocks/structure';
import { FLOW_STATE_META, useSubmissionColumns } from './submissionColumns';
import SubmissionsBulkActions from './SubmissionsBulkActions';
import PdfVersionsModal from './PdfVersionsModal';
import ReportPdfOverlay from './ReportPdfOverlay';

type SortType = 'asc' | 'desc';

// Flatten the structure tree into a flat unit list (same idiom as PeoplePage).
const flattenUnits = (units: OrgUnit[]): OrgUnit[] =>
  units.flatMap((u) => [u, ...flattenUnits(u.children ?? [])]);

// Filter option dots — semantic status tokens per value (InspectionPage idiom),
// mirroring FLOW_STATE_META's chip tones.
const FLOW_DOT: Record<SubmissionFlowState, string> = {
  [SubmissionFlowState.End]: 'var(--success)',
  [SubmissionFlowState.User]: 'var(--warning)',
  [SubmissionFlowState.Step]: 'var(--info)',
};

// Toolbar Filter category keys (rail order) + the empty selection shape.
const FILTER_KEYS = ['flowState', 'nodes', 'filledBy', 'dateRange'] as const;
const emptyFilters = (): Record<string, Set<string>> =>
  Object.fromEntries(FILTER_KEYS.map((k) => [k, new Set<string>()]));

// Stable default for `hiddenColumns` — a literal in the signature would be a
// fresh array on every render.
const NO_HIDDEN_COLUMNS: string[] = [];

interface SubmissionsListProps {
  /** Which KIND of record `recordIds` point at — the body's `type`. */
  type: SubmissionSourceType;
  /** The owning records' ids. An EMPTY array is a valid request meaning "every
   *  record of `type`", not a missing value — see the note in submissionsApi. */
  recordIds: string[];
  /** Hold the query until the parent's identity is known. Deliberately NOT
   *  derived from recordIds.length: empty is a legitimate all-records request,
   *  so inferring skip from it would permanently silence such a list. */
  skip?: boolean;
  /** The Inspections / Tasks source pills. An inspection-scoped list hides
   *  them — every row there is already inspection-sourced. Hiding them also
   *  keeps `sourceFilter` at undefined, so no stale pill can narrow the list. */
  showSourceFilter?: boolean;
  /** The toolbar Filter dropdown. Hiding it also skips its option-source
   *  queries, so a surface without it fetches nothing extra. */
  showFilterButton?: boolean;
  /** Column keys to drop from the shared pack on this surface. For columns
   *  that would render but do nothing here — not user-toggleable hiding. */
  hiddenColumns?: string[];
  /** Omitted → the REPORT icon toasts coming-soon instead of navigating. */
  onOpenReport?: (submission: Submission) => void;
  searchPlaceholder?: string;
  emptyText?: string;
  defaultLimit?: number;
}

const SubmissionsList = ({
  type,
  recordIds,
  skip = false,
  showSourceFilter = true,
  showFilterButton = true,
  hiddenColumns = NO_HIDDEN_COLUMNS,
  onOpenReport,
  searchPlaceholder,
  emptyText,
  defaultLimit = 15,
}: SubmissionsListProps) => {
  const { t } = useTranslation();

  // Verb gates — ALWAYS the `reports` module, wherever this list renders
  // (user decision: the Inspections page's Submissions tab gates on `reports`
  // too — the data is submissions, whatever surface hosts it). Download and
  // the Export CSV stub ride the `export` extra; Archive/Restore rides
  // `delete` (both are lifecycle removals — Delete itself is only a soft
  // trash, and the module has no edit verb).
  const { canDelete } = useModuleAccess('reports');
  const canExport = useCanExportReports();

  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(defaultLimit);
  const [search, setSearch] = useState('');
  const debouncedSearch = useDebouncedValue(search, 300);
  // At most one source is ever active; undefined is the "all" state and omits
  // the param from the body entirely. With the pills hidden nothing can set it,
  // so it stays undefined for the whole life of that list.
  const [sourceFilter, setSourceFilter] = useState<SubmissionSourceFilter | undefined>();
  // The Archived toggle — the body's `submissionStatus` is always one of the two states.
  const [archived, setArchived] = useState(false);
  const [filters, setFilters] = useState<Record<string, Set<string>>>(emptyFilters);
  // Custom-range FROM/TO for the `dateRange` category (ReportsPage idiom).
  const [dateRanges, setDateRanges] = useState<Record<string, FilterDropdownDateRange>>({});
  // Newest first, matching the design's active DATE SUBMITTED header. The
  // sortable columns are keyed by SubmissionSortKey itself, so this state IS the
  // request's orderBy — there is no UI-key → backend-key map to keep in sync.
  const [sort, setSort] = useState<{ key: SubmissionSortKey; type: SortType }>({
    key: SubmissionSortKey.CompleteDate,
    type: 'desc',
  });
  // id → row: the More menu's PDF report needs the picked row's id + reference
  // number, and a row picked on page 1 is no longer in `items` after paging.
  const [selected, setSelected] = useState<Map<string, Submission>>(new Map());
  // The submission whose pdf render the overlay is showing, or null when closed.
  const [pdfTarget, setPdfTarget] = useState<Submission | null>(null);
  // The submission whose version history the PDF versions modal is showing —
  // the id alone: the history response carries all the display data itself.
  const [versionsTarget, setVersionsTarget] = useState<string | null>(null);

  // Filter option sources — skipped wholesale when the caller hides the Filter
  // button, so that surface (the Inspections tab) fetches nothing new.
  const { data: structure = [] } = useGetStructureSelectionQuery(undefined, {
    skip: !showFilterButton,
  });
  const { data: companyUsers = [] } = useGetUsersSelectionQuery(undefined, {
    skip: !showFilterButton,
  });

  const filterCategories: FilterDropdownCategory[] = useMemo(
    () => [
      {
        key: 'flowState',
        label: t('submissions_col_status'),
        icon: 'circle-dot',
        options: Object.values(SubmissionFlowState).map((s) => ({
          id: s,
          label: t(FLOW_STATE_META[s].labelKey),
          dotColor: FLOW_DOT[s],
        })),
      },
      {
        key: 'nodes',
        label: t('submissions_col_node'),
        icon: 'network',
        searchable: true,
        searchPlaceholder: t('people_bulk_node_search_ph'),
        options: flattenUnits(structure)
          .filter((u) => u.level !== 0)
          .map((u) => ({ id: u.id, label: u.name })),
      },
      {
        key: 'filledBy',
        label: t('submissions_filter_filled_by'),
        icon: 'user',
        searchable: true,
        searchPlaceholder: t('members_search_people'),
        options: companyUsers.map((u) => ({ id: u.id, label: u.name })),
      },
      {
        key: 'dateRange',
        label: t('submissions_col_date'),
        icon: 'calendar',
        type: 'single',
        rangeOptionId: FormDateFilter.CUSTOM,
        defaultOptionId: DATE_ANY,
        options: buildDateFilterOptions(t),
      },
    ],
    [t, structure, companyUsers],
  );

  // Multi-select categories send bare id arrays, omitted while empty.
  const ids = (s: Set<string>) => (s.size ? [...s] : undefined);
  const dateRange = dateFilterOf(filters, 'dateRange');

  const { data, currentData, isLoading, isFetching, isError } = useGetAccessedSubmissionsQuery(
    {
      type,
      recordIds,
      submissionStatus: archived ? SubmissionListStatus.Archived : SubmissionListStatus.Active,
      sourceFilter,
      flowState: filters.flowState.size
        ? ([...filters.flowState] as SubmissionFlowState[])
        : undefined,
      companyStructureNodes: ids(filters.nodes),
      filledBy: ids(filters.filledBy),
      dateRange,
      fromDate: dateRangeOf(dateRanges, 'dateRange', dateRange, 'start'),
      toDate: dateRangeOf(dateRanges, 'dateRange', dateRange, 'end'),
      search: debouncedSearch || undefined,
      orderBy: sort,
      page,
      limit,
    },
    { skip, refetchOnMountOrArgChange: true },
  );

  // A new search / sort / source / filter / list state narrows or reorders the
  // result set — snap back to page 1 so the pager can't sit past its end.
  useEffect(() => setPage(1), [debouncedSearch, sort, sourceFilter, archived, filters, dateRanges]);

  const soon = () => toast(t('text_coming_soon'));

  // Single-select: clicking the active pill clears back to all, clicking the
  // other switches to it. Selected ids are dropped because the rows they point
  // at may not survive the new filter.
  const pickSource = (value: SubmissionSourceFilter) => {
    setSourceFilter((prev) => (prev === value ? undefined : value));
    setSelected(new Map());
  };

  // The archived list is a different row set — selected ids can't survive it.
  const toggleArchived = () => {
    setArchived((prev) => !prev);
    setSelected(new Map());
  };

  // The pdf render is the report endpoint asked with reportType `pdf` — fetched
  // only once the overlay opens for a row, then served from RTK Query's per-arg
  // cache on reopen. The list has no language toggle, so it is always `en`.
  const {
    data: pdfReport,
    isFetching: pdfFetching,
    isError: pdfError,
  } = useGetSubmissionReportQuery(
    { id: pdfTarget?.id ?? '', reportType: SubmissionReportType.Pdf, language: 'en' },
    { skip: !pdfTarget },
  );

  // Download mints a presigned link and saves the bytes — the ReportViewPage
  // flow verbatim, including the local `downloading` flag (the hook's isFetching
  // covers only the link leg, not the transfer).
  const [fetchPdfUrl] = useLazyGetSubmissionPdfUrlQuery();
  const [downloading, setDownloading] = useState(false);
  const downloadPdf = async () => {
    if (!pdfTarget) return;
    setDownloading(true);
    try {
      const url = await fetchPdfUrl({ id: pdfTarget.id, language: 'en' }).unwrap();
      await downloadFileUrl(url, `report-${pdfTarget.referenceNumber}-en.pdf`);
    } catch (err) {
      // The endpoint's failures carry a server message; downloadFileUrl's throw
      // does not and falls through to the generic copy on purpose.
      toast.error((err as ApiErrorShape)?.msg ?? t('error_generic'));
    } finally {
      setDownloading(false);
    }
  };

  // PDF report and PDF versions target ONE submission — the menu disables
  // them otherwise (the modal's meta strip is one row's identity).
  const onlySelected = () => (selected.size === 1 ? [...selected.values()][0] : undefined);
  const openPdfReport = () => {
    const only = onlySelected();
    if (only) setPdfTarget(only);
  };
  const openPdfVersions = () => {
    const only = onlySelected();
    if (only) setVersionsTarget(only.id);
  };

  const items = data?.items ?? [];
  // The API reports the record count on page-1 responses only (absent on
  // page 2+) — record it there and keep serving it while paging.
  const total = useStickyTotal(currentData?.total, page);
  const from = total === 0 ? 0 : (page - 1) * limit + 1;
  const to = Math.min(page * limit, total);

  // The columns' onOpenReport stays required — a caller that cannot open a
  // report hands over the coming-soon toast instead, so the icon renders
  // identically either way and the column pack needs no branch.
  const allColumns = useSubmissionColumns({ onSoon: soon, onOpenReport: onOpenReport ?? soon });
  const columns = allColumns.filter((c) => !hiddenColumns.includes(c.key));

  const toggleSelect = (id: string) =>
    setSelected((prev) => {
      const next = new Map(prev);
      if (next.has(id)) next.delete(id);
      else {
        const row = items.find((s) => s.id === id);
        if (row) next.set(id, row);
      }
      return next;
    });

  // Header select-all — scoped to the current page's rows, as Reports does.
  const allSelected = items.length > 0 && items.every((s) => selected.has(s.id));
  const someSelected = items.some((s) => selected.has(s.id));
  const toggleSelectAll = () =>
    setSelected((prev) => {
      const next = new Map(prev);
      if (allSelected) items.forEach((s) => next.delete(s.id));
      else items.forEach((s) => next.set(s.id, s));
      return next;
    });

  // DataTable keys row checks off a Set of ids.
  const selectedIdSet = useMemo(() => new Set(selected.keys()), [selected]);

  // Lifecycle moves — one bulk call whatever the selection size. Every one of
  // these removes the rows from the CURRENT list (archive/trash leave the
  // active list, restore leaves the archived one), so the pager steps back when
  // a full page empties (TableFooter recomputes its page count but never calls
  // onPage — the Forms unbookmark guard).
  const [bulkAction, { isLoading: bulkRunning }] = useBulkSubmissionActionMutation();
  const [deleteOpen, setDeleteOpen] = useState(false);
  const runBulk = async (action: SubmissionBulkAction, doneKey: string) => {
    const submissionIds = [...selected.keys()];
    if (!submissionIds.length) return;
    try {
      await bulkAction({ submissionIds, action }).unwrap();
      toast.success(t(doneKey, { count: submissionIds.length }));
      setSelected(new Map());
      if (page > 1 && submissionIds.length >= items.length) setPage((p) => p - 1);
    } catch (err) {
      toast.error((err as ApiErrorShape)?.msg ?? t('error_generic'));
    }
  };

  // Export CSV starts a server-side job over the selection — nothing comes
  // back to download, so success just confirms the kick-off. The selection is
  // cleared like the other bulk actions': the action on it is complete. The
  // menu item is a plain button with no loading affordance, so re-entry is
  // guarded here instead.
  const [exportCsv, { isLoading: exporting }] = useExportSubmissionsCsvMutation();
  const runExport = async () => {
    const submissionIds = [...selected.keys()];
    if (!submissionIds.length || exporting) return;
    try {
      await exportCsv(submissionIds).unwrap();
      toast.success(t('submissions_export_started', { count: submissionIds.length }));
      setSelected(new Map());
    } catch (err) {
      toast.error((err as ApiErrorShape)?.msg ?? t('error_generic'));
    }
  };

  // Download attachments — the same export-manager job family as Export CSV,
  // but for the ONE selected submission (the menu disables it otherwise).
  const [exportAttachments, { isLoading: attachmentsExporting }] =
    useExportSubmissionAttachmentsMutation();
  const runDownloadAttachments = async () => {
    const only = onlySelected();
    if (!only || attachmentsExporting) return;
    try {
      await exportAttachments(only.id).unwrap();
      toast.success(t('submissions_attachments_export_started'));
      setSelected(new Map());
    } catch (err) {
      toast.error((err as ApiErrorShape)?.msg ?? t('error_generic'));
    }
  };

  // Archive on the active list, restore on the archived one — same menu slot.
  const runArchive = () =>
    runBulk(
      archived ? SubmissionBulkAction.Active : SubmissionBulkAction.Archive,
      archived ? 'submissions_restored_done' : 'submissions_archived_done',
    );

  // Delete is a soft move to trash, behind the confirm below.
  const runDelete = async () => {
    await runBulk(SubmissionBulkAction.Trash, 'submissions_deleted_done');
    setDeleteOpen(false);
  };

  // Same key toggles direction; a new key resets to desc. DataTable types
  // onSort as (key: string), but only the three sortable columns can fire it and
  // each is keyed by a SubmissionSortKey — hence the cast at the boundary.
  const handleSort = (raw: string) => {
    const key = raw as SubmissionSortKey;
    setSort((prev) =>
      prev.key === key
        ? { key, type: prev.type === 'asc' ? 'desc' : 'asc' }
        : { key, type: 'desc' },
    );
  };

  return (
    <div className='submissions-list'>
      <div className='submissions-list__toolbar'>
        <div className='submissions-list__toolbar-lead'>
          <div className='submissions-list__search'>
            <Input
              size='sm'
              iconStart='search'
              placeholder={searchPlaceholder ?? t('submissions_search_ph')}
              aria-label={t('field_search')}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
            {search && (
              <IconButton
                className='submissions-list__search-clear'
                icon='x'
                variant='ghost'
                size='sm'
                aria-label={t('submissions_search_clear')}
                onClick={() => setSearch('')}
              />
            )}
          </div>
          {showFilterButton && (
            <FilterDropdown
              label={t('submissions_filter')}
              categories={filterCategories}
              value={filters}
              ranges={dateRanges}
              onApply={(next, ranges) => {
                setFilters(next);
                setDateRanges(ranges);
                // The narrowed list may not contain the selected rows.
                setSelected(new Map());
              }}
              emptyText={t('text_no_results_found')}
            />
          )}
          {/* Toggle buttons, not clickable Chip spans — a span with onClick takes
              no keyboard focus. `aria-pressed` is both the correct role state and
              the hook the active styling keys off. */}
          {showSourceFilter && (
            <>
              <Button
                className='submissions-list__source'
                variant='secondary'
                iconStart='clipboard-check'
                aria-pressed={sourceFilter === SubmissionSourceFilter.Inspection}
                onClick={() => pickSource(SubmissionSourceFilter.Inspection)}
              >
                {t('submissions_source_inspections')}
              </Button>
              <Button
                className='submissions-list__source'
                variant='secondary'
                iconStart='list-checks'
                aria-pressed={sourceFilter === SubmissionSourceFilter.Task}
                onClick={() => pickSource(SubmissionSourceFilter.Task)}
              >
                {t('submissions_source_tasks')}
              </Button>
            </>
          )}
        </div>
        <div className='submissions-list__toolbar-trail'>
          <span className='submissions-list__count'>
            {t('submissions_count', { shown: items.length, total })}
          </span>
          {/* Same aria-pressed idiom as the source pills: the state attribute is
              both the role state and the style hook. */}
          <Button
            className='submissions-list__archived'
            variant='secondary'
            iconStart='archive'
            aria-pressed={archived}
            onClick={toggleArchived}
          >
            {t('submissions_archived')}
          </Button>
        </div>
      </div>

      <Card padded={false} className='submissions-list__panel'>
        {selected.size > 0 && (
          <div className='submissions-list__selection-bar'>
            <div className='submissions-list__selection-lead'>
              <span className='submissions-list__selected'>
                {t('submissions_selected_count', { count: selected.size })}
              </span>
              <span className='submissions-list__selection-divider' />
              <SubmissionsBulkActions
                onPdfReport={openPdfReport}
                onPdfVersions={openPdfVersions}
                onDownloadAttachments={canExport ? runDownloadAttachments : undefined}
                pdfDisabled={selected.size !== 1}
                archived={archived}
                onExportCsv={canExport ? runExport : undefined}
                onArchive={canDelete ? runArchive : undefined}
                onDelete={canDelete ? () => setDeleteOpen(true) : undefined}
              />
            </div>
            <Button variant='ghost' onClick={() => setSelected(new Map())}>
              {t('button_clear')}
            </Button>
          </div>
        )}

        <DataTable
          columns={columns}
          rows={items}
          rowKey={(s) => s.id}
          selectable
          selectedIds={selectedIdSet}
          onToggleSelect={toggleSelect}
          allSelected={allSelected}
          someSelected={someSelected}
          onToggleSelectAll={toggleSelectAll}
          selectAllLabel={t('submissions_select_all')}
          sort={sort}
          onSort={handleSort}
          loading={isLoading || isFetching}
          error={isError}
          loadingText={t('text_loading')}
          errorText={t('error_generic')}
          minWidth={1200}
          empty={
            <>
              <Icon name='file-check' size={26} />
              {emptyText ?? t('submissions_empty')}
            </>
          }
        />

        <TableFooter
          page={page}
          limit={limit}
          total={total}
          limits={[15, 25, 50]}
          onPage={setPage}
          onLimit={(n) => {
            setLimit(n);
            setPage(1);
          }}
          perPageLabel={t('submissions_items_per_page')}
          rangeText={t('submissions_range', { from, to, total })}
        />
      </Card>

      <PdfVersionsModal submissionId={versionsTarget} onClose={() => setVersionsTarget(null)} />

      <ReportPdfOverlay
        open={!!pdfTarget}
        onClose={() => setPdfTarget(null)}
        html={pdfReport?.html}
        totalPages={pdfReport?.totalPages}
        lang='en'
        loading={pdfFetching}
        error={pdfError}
        onDownload={downloadPdf}
        downloading={downloading}
      />

      {/* One confirm for the whole selection — Delete is a soft move to trash,
          but it reads as delete to the user, hence the danger treatment. */}
      {deleteOpen && (
        <ConfirmModal
          open
          onClose={() => setDeleteOpen(false)}
          onConfirm={runDelete}
          tone='danger'
          icon='trash-2'
          title={t('submissions_delete_confirm_title', { count: selected.size })}
          description={t('submissions_delete_confirm_body')}
          confirmLabel={t('button_delete')}
          confirmVariant='danger'
          loading={bulkRunning}
        />
      )}
    </div>
  );
};

export default SubmissionsList;
